import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FolderKanban, Plus, Pin, Clock, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useProjects, type ProjectSummary } from "@/hooks/useProjects";
import { getPinnedIds } from "@/services/projectManagementService";
import { useI18n } from "@/i18n/I18nContext";

export function ProjectSwitcher() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { projects, loading } = useProjects();
  const [current, setCurrent] = useState<ProjectSummary | null>(null);

  const pinnedIds = getPinnedIds();

  const formatUpdatedAt = (iso: string | null): string | undefined => {
    if (!iso) return undefined;
    const diff    = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1)  return t("proj.switcher.justNow");
    if (minutes < 60) return t("proj.switcher.mAgo", { m: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24)   return t("proj.switcher.hAgo", { h: hours });
    return t("proj.switcher.dAgo", { d: Math.floor(hours / 24) });
  };

  const enriched = projects.map((p) => ({ ...p, pinned: pinnedIds.has(p.id) }));
  const pinned   = enriched.filter((p) => p.pinned);
  const recent   = enriched.filter((p) => !p.pinned);

  const handleSelect = (p: ProjectSummary) => {
    setCurrent(p);
    // Open the actual project workspace. Navigating to "/projects" only
    // redirects to /dashboard (App.tsx), which stranded the user back on the
    // grid instead of the project they picked.
    navigate(`/projects/${p.id}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 text-sm font-medium text-foreground/80 hover:text-foreground h-8 px-2.5"
        >
          <FolderKanban className="h-3.5 w-3.5 text-primary" />
          <span className="max-w-[140px] truncate">
            {current?.name ?? t("proj.switcher.select")}
          </span>
          {loading
            ? <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />
            : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64">
        {!loading && projects.length === 0 && (
          <DropdownMenuItem disabled className="py-3 text-muted-foreground/60 text-xs">
            {t("proj.switcher.noProjects")}
          </DropdownMenuItem>
        )}

        {pinned.length > 0 && (
          <>
            <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground/70">
              <Pin className="h-3 w-3" /> {t("proj.switcher.pinned")}
            </DropdownMenuLabel>
            {pinned.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onClick={() => handleSelect(p)}
                className="flex flex-col items-start gap-0.5 py-2"
              >
                <span className="font-medium text-sm">{p.name}</span>
                <span className="text-[11px] text-muted-foreground">{p.appType}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}

        {recent.length > 0 && (
          <>
            {pinned.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground/70">
              <Clock className="h-3 w-3" /> {t("proj.switcher.recent")}
            </DropdownMenuLabel>
            {recent.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onClick={() => handleSelect(p)}
                className="flex flex-col items-start gap-0.5 py-2"
              >
                <span className="font-medium text-sm">{p.name}</span>
                <span className="text-[11px] text-muted-foreground">
                  {p.appType}
                  {p.updatedAt ? ` · ${formatUpdatedAt(p.updatedAt)}` : ""}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/dashboard")} className="gap-2 text-primary">
          <Plus className="h-3.5 w-3.5" />
          {t("proj.switcher.create")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
